/**
 * Request Deduplication Utility
 *
 * Prevents duplicate API requests by caching in-flight requests
 * and returning the same promise for identical requests.
 *
 * Security benefits:
 * - Prevents race conditions
 * - Reduces server load from duplicate requests
 * - Mitigates accidental DoS from rapid clicking
 *
 * @module requestDeduplication
 */

class RequestDeduplicator {
  constructor() {
    // Map of request keys to pending promises
    this.pendingRequests = new Map();

    // Map of request keys to abort controllers
    this.abortControllers = new Map();

    // Request timeout (10 seconds default)
    this.defaultTimeout = 10000;
  }

  /**
   * Generate a unique key for a request
   * @param {string} url - Request URL
   * @param {object} options - Fetch options
   * @returns {string} - Unique request key
   */
  generateKey(url, options = {}) {
    const method = options.method || 'GET';
    // Fetch `body` is typically a string; JSON-stringifying strings changes the key.
    const body = options.body
      ? (typeof options.body === 'string' ? options.body : JSON.stringify(options.body))
      : '';
    const headers = options.headers ? JSON.stringify(options.headers) : '';

    // Create a unique key based on method, URL, body, and relevant headers
    return `${method}:${url}:${body}:${headers}`;
  }

  /**
   * Execute a fetch request with deduplication
   * @param {string} url - Request URL
   * @param {object} options - Fetch options
   * @param {number} timeout - Request timeout in milliseconds
   * @returns {Promise} - Fetch promise
   */
  async fetch(url, options = {}, timeout = this.defaultTimeout) {
    const key = this.generateKey(url, options);

    // If request is already pending, return the existing promise
    if (this.pendingRequests.has(key)) {
      console.debug(`[RequestDedup] Reusing pending request: ${key}`);
      return this.pendingRequests.get(key);
    }

    // Create abort controller for this request
    const abortController = new AbortController();
    this.abortControllers.set(key, abortController);

    // Add abort signal to options
    const fetchOptions = {
      ...options,
      signal: abortController.signal
    };

    // Create timeout promise
    const timeoutId = setTimeout(() => {
      abortController.abort();
      console.warn(`[RequestDedup] Request timed out: ${key}`);
    }, timeout);

    const makeAbortError = () => {
      try {
        return new DOMException('The operation was aborted.', 'AbortError');
      } catch {
        const err = new Error('The operation was aborted.');
        err.name = 'AbortError';
        return err;
      }
    };

    // Ensure cancellation rejects even if a mocked fetch doesn't honor AbortController.
    const abortPromise = new Promise((_, reject) => {
      const onAbort = () => reject(makeAbortError());
      abortController.signal.addEventListener('abort', onAbort, { once: true });
    });

    // Execute the fetch request
    const requestPromise = Promise.race([
      globalThis.fetch(url, fetchOptions),
      abortPromise,
    ])
      .then((response) => {
        clearTimeout(timeoutId);
        return response;
      })
      .catch((error) => {
        clearTimeout(timeoutId);
        throw error;
      })
      .finally(() => {
        // Clean up after request completes
        this.pendingRequests.delete(key);
        this.abortControllers.delete(key);
      });

    // Store the pending request
    this.pendingRequests.set(key, requestPromise);

    return requestPromise;
  }

  /**
   * Cancel a specific request
   * @param {string} url - Request URL
   * @param {object} options - Fetch options used to generate key
   */
  cancel(url, options = {}) {
    const key = this.generateKey(url, options);
    const controller = this.abortControllers.get(key);

    if (controller) {
      controller.abort();
      this.pendingRequests.delete(key);
      this.abortControllers.delete(key);
      console.debug(`[RequestDedup] Cancelled request: ${key}`);
    }
  }

  /**
   * Cancel all pending requests
   */
  cancelAll() {
    console.debug(`[RequestDedup] Cancelling ${this.abortControllers.size} pending requests`);

    this.abortControllers.forEach((controller) => {
      controller.abort();
    });

    this.pendingRequests.clear();
    this.abortControllers.clear();
  }

  /**
   * Get the number of pending requests
   * @returns {number}
   */
  getPendingCount() {
    return this.pendingRequests.size;
  }

  /**
   * Check if a specific request is pending
   * @param {string} url - Request URL
   * @param {object} options - Fetch options
   * @returns {boolean}
   */
  isPending(url, options = {}) {
    const key = this.generateKey(url, options);
    return this.pendingRequests.has(key);
  }
}

// Singleton instance
const requestDeduplicator = new RequestDeduplicator();

/**
 * Deduplicated fetch wrapper
 * Drop-in replacement for window.fetch with automatic deduplication
 *
 * @param {string} url - Request URL
 * @param {object} options - Fetch options
 * @param {number} timeout - Optional timeout in milliseconds
 * @returns {Promise<Response>}
 *
 * @example
 * // Replace: fetch('/api/data', { method: 'POST', body: JSON.stringify(data) })
 * // With:    deduplicatedFetch('/api/data', { method: 'POST', body: JSON.stringify(data) })
 */
export const deduplicatedFetch = (url, options, timeout) => {
  return requestDeduplicator.fetch(url, options, timeout);
};

/**
 * Cancel a specific request
 * @param {string} url - Request URL
 * @param {object} options - Fetch options
 */
export const cancelRequest = (url, options) => {
  requestDeduplicator.cancel(url, options);
};

/**
 * Cancel all pending requests
 */
export const cancelAllRequests = () => {
  requestDeduplicator.cancelAll();
};

/**
 * Get pending request count
 * @returns {number}
 */
export const getPendingRequestCount = () => {
  return requestDeduplicator.getPendingCount();
};

/**
 * Check if a request is pending
 * @param {string} url - Request URL
 * @param {object} options - Fetch options
 * @returns {boolean}
 */
export const isRequestPending = (url, options) => {
  return requestDeduplicator.isPending(url, options);
};

// Export singleton for advanced usage
export default requestDeduplicator;
